import { useCallback, useEffect, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import { Button, Field, Input, useToast } from "../../ui";
import { HubFormCard } from "../AdminPortal/HubPwa";
import s from "../clinic.module.css";
import md from "../common/manageDesktop.module.css";

function emptyDraft(group) {
  return {
    id: group.id,
    name: group.name,
    auto: !!group.attendance_poll_auto,
    hours: group.attendance_poll_hours_before ?? 2,
  };
}

export default function AttendancePollSettingsPanel({ compact = false }) {
  const toast = useToast();
  const [groups, setGroups] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await clinicAPI.getSpecializations();
      const list = (data.specializations || []).filter((g) => g.is_active !== false);
      setGroups(list);
      setDrafts(list.map(emptyDraft));
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось загрузить группы");
      setGroups([]);
      setDrafts([]);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const setDraft = (id, patch) => {
    setDrafts((rows) =>
      rows.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    );
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const changed = drafts.filter((row) => {
        const orig = groups.find((g) => g.id === row.id);
        if (!orig) return false;
        return (
          row.auto !== !!orig.attendance_poll_auto ||
          Number(row.hours) !== Number(orig.attendance_poll_hours_before ?? 2)
        );
      });
      if (!changed.length) {
        toast.info("Изменений нет");
        return;
      }
      await Promise.all(
        changed.map((row) =>
          clinicAPI.updateSpecialization(row.id, {
            attendance_poll_auto: row.auto,
            attendance_poll_hours_before: row.hours,
          }),
        ),
      );
      toast.success("Настройки опросов сохранены");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  const enableAll = () => {
    setDrafts((rows) => rows.map((row) => ({ ...row, auto: true })));
  };

  const body = loading ? (
    <p className={s.summaryEmptyNote}>Загрузка групп…</p>
  ) : drafts.length === 0 ? (
    <p className={s.summaryEmptyNote}>
      Сначала создайте группу — здесь настроите авто-опрос о явке.
    </p>
  ) : (
    <>
      <p className={compact ? s.onboardingSheetHint : md.desktopGroupSettingsLead}>
        За указанное число часов до тренировки клиентам придёт push и опрос «Приду» в
        приложении. Если занятие добавили позже — отправьте опрос вручную из календаря.
      </p>
      <div className={s.attendancePollToolbar}>
        <Button type="button" size="sm" variant="secondary" onClick={enableAll}>
          Включить для всех
        </Button>
      </div>
      <ul className={s.attendancePollGroupList}>
        {drafts.map((row) => (
          <li key={row.id} className={s.attendancePollGroupRow}>
            <label className={s.settingsToggleRow}>
              <input
                type="checkbox"
                checked={row.auto}
                onChange={(e) => setDraft(row.id, { auto: e.target.checked })}
              />
              <span>
                <strong>{row.name}</strong>
              </span>
            </label>
            <Field label="За сколько часов">
              <Input
                type="number"
                min={1}
                max={24}
                value={row.hours}
                disabled={!row.auto}
                onChange={(e) => setDraft(row.id, { hours: e.target.value })}
              />
            </Field>
          </li>
        ))}
      </ul>
      <Button type="button" size="sm" disabled={saving} onClick={saveAll}>
        {saving ? "Сохранение…" : "Сохранить"}
      </Button>
    </>
  );

  if (compact) {
    return <HubFormCard title="Опрос о явке">{body}</HubFormCard>;
  }

  return (
    <section className={md.desktopSettingsNotifyCard}>
      <h3 className={md.desktopSettingsSubheading}>Опрос о явке</h3>
      {body}
    </section>
  );
}
